import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/services/api.client'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useFilteredQuery } from '@/hooks/useFilteredQuery'
import { FilterBar } from '@/components/FilterBar'

interface Contractor {
	contractorId: string
	name: string
	code: string
	isActive: boolean
	createdAt: string
	updatedAt: string
}

interface ContractorFormData {
	name: string
	code: string
	isActive?: boolean
}

export function ContractorsPage() {
	const queryClient = useQueryClient()
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [editingContractor, setEditingContractor] = useState<Contractor | null>(null)
	const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
	const [formData, setFormData] = useState<ContractorFormData>({ name: '', code: '' })

	const {
		data: contractors,
		isLoading,
		tempFilters,
		setTempFilters,
		applyFilters,
		clearFilters,
	} = useFilteredQuery<Contractor>({
		endpoint: '/contractors',
		queryKey: 'contractors',
	})

	const createMutation = useMutation({
		mutationFn: async (data: ContractorFormData) => {
			const response = await apiClient.post<Contractor>('/contractors', data)
			return response.data
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['contractors'] })
			setShowCreateModal(false)
			setFormData({ name: '', code: '' })
		},
	})

	const updateMutation = useMutation({
		mutationFn: async ({ id, data }: { id: string; data: Partial<ContractorFormData> }) => {
			const response = await apiClient.put<Contractor>(`/contractors/${id}`, data)
			return response.data
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['contractors'] })
			setEditingContractor(null)
			setFormData({ name: '', code: '' })
		},
	})

	const deleteMutation = useMutation({
		mutationFn: async (id: string) => {
			await apiClient.delete(`/contractors/${id}`)
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['contractors'] })
			setDeleteConfirm(null)
		},
	})

	const openEdit = (contractor: Contractor) => {
		setEditingContractor(contractor)
		setFormData({ name: contractor.name, code: contractor.code, isActive: contractor.isActive })
	}

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<h1 className="text-2xl font-bold text-gray-900">Contractors</h1>
				<Button
					onClick={() => {
						setFormData({ name: '', code: '' })
						setShowCreateModal(true)
					}}
				>
					Create Contractor
				</Button>
			</div>

			<FilterBar
				filters={[
					{ key: 'name', label: 'Name', type: 'text' },
					{ key: 'code', label: 'Code', type: 'text' },
				]}
				values={tempFilters}
				onChange={setTempFilters}
				onApply={applyFilters}
				onClear={clearFilters}
			/>

			<div className="bg-white rounded-lg shadow overflow-hidden">
				{isLoading ? (
					<div className="p-8 text-center text-gray-500">Loading...</div>
				) : !contractors || contractors.length === 0 ? (
					<div className="p-8 text-center text-gray-500">No contractors found</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Code</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Created</TableHead>
								<TableHead>Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{contractors.map((c) => (
								<TableRow key={c.contractorId}>
									<TableCell className="font-medium">{c.name}</TableCell>
									<TableCell>
										<code className="text-sm bg-gray-100 px-1 rounded">{c.code}</code>
									</TableCell>
									<TableCell>
										<Badge className={c.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
											{c.isActive ? 'Active' : 'Inactive'}
										</Badge>
									</TableCell>
									<TableCell className="text-sm text-gray-500">{new Date(c.createdAt).toLocaleDateString()}</TableCell>
									<TableCell>
										<div className="flex gap-1">
											<Button variant="ghost" size="sm" onClick={() => openEdit(c)}>
												Edit
											</Button>
											<Button
												variant="ghost"
												size="sm"
												className="text-red-600"
												onClick={() => setDeleteConfirm(c.contractorId)}
											>
												Delete
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</div>

			{/* Create Modal */}
			{showCreateModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
						<h2 className="text-lg font-bold mb-4">Create Contractor</h2>
						<div className="space-y-3">
							<div>
								<label className="block text-sm font-medium mb-1">Name *</label>
								<Input
									value={formData.name}
									onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
								/>
							</div>
							<div>
								<label className="block text-sm font-medium mb-1">Code *</label>
								<Input
									value={formData.code}
									onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
								/>
							</div>
						</div>
						<div className="flex justify-end gap-2 mt-6">
							<Button variant="outline" onClick={() => setShowCreateModal(false)}>
								Cancel
							</Button>
							<Button
								onClick={() => createMutation.mutate(formData)}
								disabled={!formData.name || !formData.code || createMutation.isPending}
							>
								{createMutation.isPending ? 'Creating...' : 'Create'}
							</Button>
						</div>
						{createMutation.isError && <p className="text-sm text-red-600 mt-2">Failed to create contractor</p>}
					</div>
				</div>
			)}

			{/* Edit Modal */}
			{editingContractor && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
						<h2 className="text-lg font-bold mb-4">Edit Contractor</h2>
						<div className="space-y-3">
							<div>
								<label className="block text-sm font-medium mb-1">Name</label>
								<Input
									value={formData.name}
									onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
								/>
							</div>
							<div>
								<label className="block text-sm font-medium mb-1">Code</label>
								<Input
									value={formData.code}
									onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
								/>
							</div>
							<div className="flex items-center gap-2">
								<input
									type="checkbox"
									checked={formData.isActive ?? true}
									onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
								/>
								<label className="text-sm">Active</label>
							</div>
						</div>
						<div className="flex justify-end gap-2 mt-6">
							<Button variant="outline" onClick={() => setEditingContractor(null)}>
								Cancel
							</Button>
							<Button
								onClick={() => updateMutation.mutate({ id: editingContractor.contractorId, data: formData })}
								disabled={updateMutation.isPending}
							>
								{updateMutation.isPending ? 'Saving...' : 'Save'}
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* Delete Confirmation */}
			{deleteConfirm && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
						<h2 className="text-lg font-bold mb-2">Delete Contractor?</h2>
						<p className="text-sm text-gray-500 mb-4">This action cannot be undone.</p>
						<div className="flex justify-end gap-2">
							<Button variant="outline" onClick={() => setDeleteConfirm(null)}>
								Cancel
							</Button>
							<Button
								variant="destructive"
								onClick={() => deleteMutation.mutate(deleteConfirm)}
								disabled={deleteMutation.isPending}
							>
								{deleteMutation.isPending ? 'Deleting...' : 'Delete'}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
